/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useContext, useEffect } from 'react';
import {
    object, string, shape, arrayOf, number, func, bool, node,
} from 'prop-types';
import { Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ReactHTMLParser from 'react-html-parser';
import ConnectedGraphqlProductSkus from '../../../GraphqlPDPSkus/GraphqlProductSkus';
import ProductReview from '../../../ProductReview';
import DesktopMobile from '../../../../../../../helpers/DesktopMobile/DesktopMobile';
import PassportBannerUI from '../../../../../../../pages/Checkout/containers/CheckoutFloral/Partials/Passport/PassportBannerUI/PassportBannerUI';
import ProductImageUI from '../../../ProductImageUI';
import { getBaseCode } from '../../../../../../../helpers/tracking/product/dataLayerProductHelpers';
import KlarnaCreditPromotionBadge from '../../../../../../Checkout/KlarnaPaymentOnSiteMessage/KlarnaCreditPromotionBadge/KlarnaCreditPromotionBadge';
import PassportBannerContainer from '../../../StandardProductsPage/PassportBannerContainer';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import QuestionsAndAnswersAI from '../../../QuestionsAndAnswers/QuestionsAndAnswersAI';

const styles = ({
    breakpoints, palette, spacing,
}) => ({
    productImageContainer: {
        padding: `0px ${spacing(2)}px`,
        boxSizing: 'border-box',
        '& > div': {
            '& > div:first-of-type': {
                margin: `${spacing(2)}px auto`,
            },
        },
    },
    prodName: {
        fontSize: '2em',
        fontWeight: '800',
        margin: '0px 0px 5px 0px',
        [breakpoints.down('md')]: {
            fontSize: '1.5em',
        },
        [breakpoints.down('sm')]: {
            fontSize: '1.25em',
            fontWeight: '600',
            margin: '0px 0px 10px 0px',
        },
    },
    prodAvailability: {
        color: palette.cms?.skuAvailableToShipTextColor || palette.colorNeutral70 || '#757575',
        fontSize: '0.68em',
        fontWeight: 600,
        [breakpoints.down('sm')]: {
            color: palette.cms?.skuAvailableToShipTextColor || palette.colorNeutral70 || '#757575',
            padding: '5px 0 10px 0',
            fontSize: '0.9em',
            fontWeight: '600',
        },
    },
    partNumber: {
        fontSize: '0.93em',
        padding: '2px 0px',
    },
    prodPrice: {
        fontSize: '1.1em',
        fontWeight: '700',
        [breakpoints.down('sm')]: {
            color: palette.colorSecondary || '#600',
        },
    },
    mobileProdDetails: {
        padding: '0 10px 0 10px',
    },
    shortDescription: {
        [breakpoints.down(959)]: {
            paddingRight: '10px',
        },
    },
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        fontWeight: '400',
        fontSize: '1.1em',
    },
    salePrice: {
        fontWeight: '400',
        color: palette.pdp.salePrice || '#a1001a',
        padding: '0 0 0 10px',
        fontSize: '1.1em',
    },
    defaultPrice: {
        fontSize: '1.25em',
    },
    offPrice: {
        fontWeight: '400',
        right: '72px',
        fontSize: '1.1em',
        padding: '0 0 0 10px',
        color: palette.contentColor,
    },
    passportContainer: {
        marginTop: spacing(3),
        '& *': {
            float: 'unset',
        },
    },
    mobilePassportContainer: {
        width: '100%',
        padding: '0px 5px',
        boxSizing: 'border-box',
    },
    selectionButton: {
        width: '90%',
        maxWidth: '360px',
        display: 'block',
        height: '50px',
        fontWeight: 600,
        fontSize: '1.1em',
        borderRadius: '30px',
        margin: `${spacing(4)}px ${spacing(2)}px ${spacing(3)}px auto`,
    },
    mainContainer: {
        width: '100%',
    },
    yotpoReview: {
        marginBottom: 8,
    },
    yotpoReviewMobile: {
        marginBottom: 16,
    },
});

const CYOProductDetails = ({
    addonContainer,
    classes,
    data,
    selectedSku,
    skuData,
    content,
    presentation_family,
    handleSkuChange,
    handleMakeSelection,
    location,
    brand,
    passportMemberImg,
    isPassportBundleProduct,
    url,
    productBaseContent,
}) => {
    let skuConfig = {};
    // let deliveryData = {};
    // let passportMemberImg = '';
    let productUrl = '';

    if (content?.entries?.[0]) {
        skuConfig = content.entries[0].sku_section;
        // deliveryData = content.entries[0].delivery_section;
        // passportMemberImg = content.entries[0].passport_banner_section?.member_image;
        productUrl = content.entries[0].url;
    }

    const { featureFlags = {} } = useContext(CrossMerch);
    const isPdpYotpoEnbaled = Boolean(featureFlags?.['is-pdp-yotpo-enabled']);
    const isPdpPowerReviewsEnabled = Boolean(featureFlags?.['is-pdp-reviews-enabled']);
    const isPowerReviewEnabled = Boolean(featureFlags?.['is-power-review-enabled']);

    // This needs to be updated or deleted once PR is in Production
    useEffect(() => {
        if (typeof window !== 'undefined' && window?.yotpo?.refreshWidgets && typeof window.yotpo.refreshWidgets === 'function') {
            // Call refresh only if yotpo initialized
            if (window.yotpo.initialized) {
                window.yotpo.refreshWidgets();
            }
        }
    }, [data]);

    // TODO: Determine YOTPO works without commented out lines
    // const buildImgPath = (path, name) => `${path + name}x.jpg`;
    const {
        availability,
        name,
        partNumber,
        shortDescription,
    } = data;
    const imageName = data.image?.name ?? '';
    const imagePath = selectedSku?.image?.path ?? '';
    // const heroImage = buildImgPath(imagePath, imageName) ?? '';
    const deliveryMessage = availability?.deliveryMessage;
    const questionAndAnswersAIEnabled = featureFlags['ai-questions-and-answers-pdp'];

    // Gets the value of sale price from the sku.
    const renderSkuPrice = () => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const retailPrice = skuData?.[0]?.prices?.find((priceData) => priceData.type === 'retail')?.value;
        const salePrice = skuData?.[0]?.prices?.find((priceData) => priceData.type === 'sale')?.value;
        const defaultPrice = `$${skuData?.[0]?.prices?.[0]?.value}`;
        const offPrice = `${round(retailPrice - salePrice)} off` || '';
        return (
            <span className={classes.setItemLayout}>
                {salePrice && retailPrice && salePrice !== retailPrice
                    ? (
                        <>
                            <s>
                                ${retailPrice}
                            </s>
                            <span className={classes.salePrice}>
                                ${salePrice}
                            </span>
                            <span className={classes.offPrice}>
                                ${offPrice}
                            </span>

                        </>
                    )
                    : <span className={classes.defaultPrice}><strong>{defaultPrice}</strong></span>}
            </span>
        );
    };

    const renderProductReview = ({ mobile }) => (isPowerReviewEnabled ? (
        <div className={mobile ? classes.yotpoReviewMobile : classes.yotpoReview}>
            <ProductReview
                showUnderTitle
                product={data}
                selectedSku={selectedSku}
                partNumber={getBaseCode(data.partNumber)}
                name={data.name}
                productUrl={url}
                image={{
                    path: imagePath,
                    name: imageName,
                }}
            />
        </div>
    ) : <></>);

    const desktopProductDetails = () => (
        <Grid container spacing={4} data-testid="cyoDesktopDetails" className={classes.mainContainer}>
            <Grid item md={6}>
                <ProductImageUI
                    data={data}
                    selectedSku={selectedSku}
                    location={location}
                    presentation_family={presentation_family}
                    product={data}
                    brand={brand}
                    content={content}
                    smartGiftLetThemKnowSection={{}}
                    productBaseContent={productBaseContent}
                    productName={data.name}
                    productNameDisplay={data.name}
                    isSmartGiftEnabled={false}
                />
            </Grid>
            <Grid item md={6}>
                <h1 className={classes.prodName}>{ReactHTMLParser(name) || ''}</h1>
                {isPdpPowerReviewsEnabled && renderProductReview({ mobile: false })}
                {(skuData?.length > 1) ? (
                    <ConnectedGraphqlProductSkus
                        skuData={skuData}
                        skuConfig={skuConfig}
                        handleSkuChange={handleSkuChange}
                        selectedSku={selectedSku}
                        presentation_family={presentation_family}
                        product={data}
                        productUrl={productUrl}
                        location={location}
                        brand={brand}
                        isPdpYotpoEnabled
                        isCyoProduct
                    />
                ) : (
                    <>
                        <div className={classes.prodAvailability}>{deliveryMessage || ''}</div>
                        <div className={classes.partNumber}>{partNumber || ''}</div>
                        <div className={classes.prodPrice}>{renderSkuPrice()}</div>
                        <KlarnaCreditPromotionBadge
                            selectedSku={selectedSku}
                        />
                    </>
                )}
                {addonContainer()}
                <div className={classes.passportContainer}>
                    <PassportBannerContainer selectedSku={selectedSku}>
                        <PassportBannerUI
                            bannerOnPage="product"
                            productName={name}
                            passportMemberImg={passportMemberImg}
                            presentation_family={presentation_family}
                            isPassportBundleProduct={isPassportBundleProduct || {}}
                        />
                    </PassportBannerContainer>
                </div>

                <Button data-testid="cyoDesktopSelectionsButton" color="secondary" variant="contained" onClick={handleMakeSelection} className={classes.selectionButton}>
                    Make Your Selections
                </Button>
                <div className={classes.shortDescription}>{ReactHTMLParser(shortDescription) || ''}</div>
                {questionAndAnswersAIEnabled ? (
                    <QuestionsAndAnswersAI
                        product={classes}
                        selectedSku={selectedSku}
                        featureFlags={featureFlags}
                    />
                ) : null}

            </Grid>
        </Grid>
    );
    const mobileProductDetails = () => (
        <Grid container spacing={0} data-testid="cyoMobileDetails">
            <Grid className={classes.productImageContainer} justify="center" item xs={12}>
                <ProductImageUI
                    data={data}
                    selectedSku={selectedSku}
                    location={location}
                    presentation_family={presentation_family}
                    product={data}
                    brand={brand}
                    content={content}
                    smartGiftLetThemKnowSection={{}}
                    productBaseContent={productBaseContent}
                    productName={data.name}
                    productNameDisplay={data.name}
                    isSmartGiftEnabled={false}
                />
            </Grid>
            <Grid item xs={12} className={classes.mobileProdDetails}>
                <h1 className={classes.prodName}>{ReactHTMLParser(name) || ''}</h1>
                {isPdpPowerReviewsEnabled && renderProductReview({ mobile: true })}
                {(skuData?.length > 1) ? (
                    <ConnectedGraphqlProductSkus
                        skuData={skuData}
                        skuConfig={skuConfig}
                        handleSkuChange={handleSkuChange}
                        selectedSku={selectedSku}
                        presentation_family={presentation_family}
                        product={data}
                        productUrl={productUrl}
                        location={location}
                        brand={brand}
                        isCyoProduct
                    />
                ) : (
                    <>
                        <div className={classes.partNumber}>{partNumber.split('-')[2] || ''}</div>
                        <div className={classes.prodPrice}>{renderSkuPrice()}</div>
                        <div className={classes.prodAvailability}>{deliveryMessage || ''}</div>
                        <KlarnaCreditPromotionBadge
                            selectedSku={selectedSku}
                        />
                    </>
                )}
                {questionAndAnswersAIEnabled ? (
                    <QuestionsAndAnswersAI
                        product={classes}
                        selectedSku={selectedSku}
                        featureFlags={featureFlags}
                    />
                ) : null}
            </Grid>
            <Grid item xs={12} className={classes.mobilePassportContainer}>
                {addonContainer()}
            </Grid>
            <Grid item xs={12} className={classes.mobilePassportContainer}>
                <PassportBannerContainer selectedSku={selectedSku}>
                    <PassportBannerUI
                        bannerOnPage="product"
                        productName={name}
                        passportMemberImg={passportMemberImg}
                        presentation_family={presentation_family}
                        isPassportBundleProduct={isPassportBundleProduct || {}}
                    />
                </PassportBannerContainer>
            </Grid>
        </Grid>
    );

    return (
        <DesktopMobile
            desktop={desktopProductDetails}
            mobile={mobileProductDetails}
        />
    );
};

CYOProductDetails.propTypes = {
    addonContainer: node,
    classes: object.isRequired,
    handleMakeSelection: func.isRequired,
    data: shape({
        name: string.isRequired,
        partNumber: string.isRequired,
        shortDescription: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }).isRequired,
        availability: shape({
            deliveryMessage: string.isRequired,
            productDeliveryType: string.isRequired,
        }).isRequired,
    }).isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    brand: shape({
        domain: string.isRequired,
    }).isRequired,
    content: shape({
        entries: arrayOf(shape({
            sku_section: object.isRequired,
            url: string.isRequired,
        })),
    }).isRequired,
    presentation_family: string.isRequired,
    handleSkuChange: func.isRequired,
    location: object.isRequired,
    productBaseContent: object,
    url: string,
    passportMemberImg: shape({
        url: string,
        title: string,
    }),
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
};

CYOProductDetails.defaultProps = {
    addonContainer: () => null,
    passportMemberImg: {},
    isPassportBundleProduct: {},
    url: '',
    productBaseContent: {},
};

export default withStyles(styles)(CYOProductDetails);
